import { useState } from 'react'
import './AISecScripts.css'

const AISecScripts = () => {
  const [copiedId, setCopiedId] = useState(null)

  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedId(id)
      setTimeout(() => setCopiedId(null), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const ScriptCard = ({ country, audience, tone, dms, idPrefix }) => {
    return (
      <div className="script-card">
        <div className="script-header">
          <h5>{country}</h5>
          <div className="script-meta">
            <span className="audience">Audience: {audience}</span>
            <span className="tone">Tone: {tone}</span>
          </div>
        </div>
        {dms.map((dm, index) => {
          const fullText = dm.text
          const scriptId = `${idPrefix}-dm${index + 1}`
          return (
            <div key={index} className="dm-script">
              <div className="dm-header">
                <span className="dm-label">{dm.label}</span>
                <button
                  className="copy-btn"
                  onClick={() => copyToClipboard(fullText, scriptId)}
                  title="Copy to clipboard"
                >
                  {copiedId === scriptId ? '✓' : '📋'}
                </button>
              </div>
              <div className="dm-text">{fullText}</div>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div className="aisec-scripts-page">
      <div className="page-header">
        <div>
          <h2>📝 AI Security LinkedIn DM Scripts</h2>
          <p className="subtitle">Ready-to-use LinkedIn DM templates organized by region and country</p>
        </div>
      </div>

      <div className="scripts-container">
        {/* EU Scripts */}
        <section className="scripts-section">
          <h3>🇪🇺 EU-Specific LinkedIn DM Scripts</h3>
          
          <ScriptCard
            country="🇩🇪 Germany — Enterprise / Compliance-Led"
            audience="CISO, Head of IT Security, Compliance, Risk"
            tone="Formal, structured, regulation-aware"
            idPrefix="de"
            dms={[
              {
                label: "DM #1 – Opener",
                text: "Hi {{Name}},\nquick question, with the EU AI Act and NIS2 coming into force, are your teams already receiving structured AI security or governance training, or is that still being assessed internally?"
              },
              {
                label: "DM #2 – Value Framing",
                text: "Many German organisations we speak to are realising that existing security training doesn't fully cover AI-specific risks (model misuse, data leakage, governance).\nWe help teams with a practical AI security framework mapped to EU requirements, not generic awareness."
              },
              {
                label: "DM #3 – Soft CTA",
                text: "If useful, I can share a short overview of how others are approaching AI security readiness without overloading teams."
              }
            ]}
          />

          <ScriptCard
            country="🇫🇷 France — Sovereignty & Trust-Focused"
            audience="Security, Legal, Public Sector"
            tone="Credibility, ethics, sovereignty"
            idPrefix="fr"
            dms={[
              {
                label: "DM #1",
                text: "Bonjour {{Name}},\nare AI security and governance already formally addressed in your organisation, or still handled within existing cybersecurity policies?"
              },
              {
                label: "DM #2",
                text: "We're seeing French organisations focus more on AI sovereignty, governance, and defensibility, especially ahead of regulatory scrutiny."
              },
              {
                label: "DM #3",
                text: "Happy to share a concise framework showing how teams structure AI security responsibilities across technical and governance roles."
              }
            ]}
          />

          <ScriptCard
            country="🇳🇱 Netherlands — Pragmatic & Fast-Moving"
            audience="CISO, Cloud Security, Product Security"
            tone="Direct, efficient"
            idPrefix="nl"
            dms={[
              {
                label: "DM #1",
                text: "Hi {{Name}}, quick check, do your teams already have AI-specific security training, or are you still relying on traditional AppSec / cloud security guidance?"
              },
              {
                label: "DM #2",
                text: "Many Dutch SaaS and cloud-native teams tell us AI changes their threat model faster than policy updates can keep up."
              },
              {
                label: "DM #3",
                text: "I can send a short example of how teams are closing that gap with targeted AI security training if helpful."
              }
            ]}
          />

          <ScriptCard
            country="🇸🇪 Sweden — Ethics & Responsible AI"
            audience="AI Leads, Security Architects"
            tone="Progressive, responsible innovation"
            idPrefix="se"
            dms={[
              {
                label: "DM #1",
                text: "Hi {{Name}},\nwith responsible AI becoming more visible, are your teams already trained on AI-specific security and misuse risks?"
              },
              {
                label: "DM #2",
                text: "We see strong interest in combining ethical AI with practical security controls, especially for product teams."
              },
              {
                label: "DM #3",
                text: "Happy to share a brief overview if you'd like to see how others approach this without slowing innovation."
              }
            ]}
          />

          <ScriptCard
            country="🇵🇱 Poland / 🇪🇸 Spain / 🇮🇹 Italy — Mixed (Individual → Team)"
            audience="Security consultants, managers"
            tone="Career + compliance blend"
            idPrefix="pl-es-it"
            dms={[
              {
                label: "DM #1",
                text: "Hi {{Name}}, are you personally working with AI systems today, or supporting clients who are starting to ask AI-security questions?"
              },
              {
                label: "DM #2",
                text: "We're seeing consultants upskill early in AI security because EU clients are starting to demand it explicitly."
              },
              {
                label: "DM #3",
                text: "If useful, I can share a short resource showing what skills are becoming most requested."
              }
            ]}
          />
        </section>

        {/* Non-EU Scripts */}
        <section className="scripts-section">
          <h3>🌍 Non-EU LinkedIn DM Scripts</h3>
          
          <ScriptCard
            country="🇺🇸 United States — Risk & Liability-Driven"
            audience="CISO, VP Security, Risk Leaders"
            tone="Direct, outcome-oriented"
            idPrefix="us"
            dms={[
              {
                label: "DM #1",
                text: "Hi {{Name}}, quick question, are your teams already trained on AI-specific security risks, or is AI still being treated as part of general AppSec?"
              },
              {
                label: "DM #2",
                text: "Many US orgs are realising AI introduces new exposure that auditors, regulators, and plaintiffs will focus on."
              },
              {
                label: "DM #3",
                text: "We help teams build defensible AI security capability without turning it into a massive program, happy to share an example."
              }
            ]}
          />

          <ScriptCard
            country="🇬🇧 United Kingdom — Governance & Audit Readiness"
            audience="Risk, Compliance, Security"
            tone="Professional, pragmatic"
            idPrefix="uk"
            dms={[
              {
                label: "DM #1",
                text: "Hi {{Name}},\nare you already addressing AI security and governance explicitly, or is it still bundled into existing cyber training?"
              },
              {
                label: "DM #2",
                text: "We're seeing UK firms prepare now so they're not caught off-guard by AI-related audit or regulatory questions."
              },
              {
                label: "DM #3",
                text: "Happy to send a short framework overview if useful."
              }
            ]}
          />

          <ScriptCard
            country="🇮🇳 India — Individual / Career-Focused"
            audience="Developers, Security Engineers"
            tone="Opportunity-driven"
            idPrefix="in"
            dms={[
              {
                label: "DM #1",
                text: "Hi {{Name}}, quick question, are you already working with AI systems, or planning to move into AI-related security work?"
              },
              {
                label: "DM #2",
                text: "AI security is becoming a differentiator for senior engineering and security roles globally."
              },
              {
                label: "DM #3",
                text: "If helpful, I can share a short roadmap showing what skills companies are starting to expect."
              }
            ]}
          />

          <ScriptCard
            country="🇸🇬 Singapore — Premium & Forward-Looking"
            audience="GovTech, Finance, Security Leaders"
            tone="Strategic, leadership-focused"
            idPrefix="sg"
            dms={[
              {
                label: "DM #1",
                text: "Hi {{Name}}, are AI security and governance already formalised in your organisation, or still evolving?"
              },
              {
                label: "DM #2",
                text: "We see Singapore organisations focusing early on AI governance maturity rather than reacting later."
              },
              {
                label: "DM #3",
                text: "Happy to share a concise example if useful."
              }
            ]}
          />

          <ScriptCard
            country="🇦🇪 UAE / 🇸🇦 Saudi Arabia — National Strategy-Aligned"
            audience="Government, Digital Authorities"
            tone="Strategic, respectful"
            idPrefix="ae-sa"
            dms={[
              {
                label: "DM #1",
                text: "Hello {{Name}},\nare AI security and governance currently being standardised across teams, or handled at project level?"
              },
              {
                label: "DM #2",
                text: "Many organisations align training closely with national AI strategies and risk management goals."
              },
              {
                label: "DM #3",
                text: "I can share a brief overview of how structured AI security training supports that if helpful."
              }
            ]}
          />
        </section>

        {/* Usage Guidelines */}
        <section className="scripts-section">
          <h3>🎯 How to Use These Effectively</h3>
          
          <div className="guidelines-grid">
            <div className="guideline-card">
              <h4>Golden Rules</h4>
              <ul>
                <li className="do">✅ Don't pitch on DM #1</li>
                <li className="do">✅ Ask one simple question</li>
                <li className="do">✅ Provide value before links</li>
                <li className="dont">❌ No attachments until invited</li>
              </ul>
            </div>

            <div className="guideline-card">
              <h4>Conversion Path</h4>
              <div className="conversion-path">
                <span className="path-step">DM</span>
                <span className="path-arrow">→</span>
                <span className="path-step">Framework overview</span>
                <span className="path-arrow">→</span>
                <span className="path-step">Call</span>
                <span className="path-arrow">→</span>
                <span className="path-step">Team license</span>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}

export default AISecScripts

